/**
 * Vigia de esgotados — cálculo da tela (sem React/MUI): rótulos, filtros e resumo.
 */
import type { Acao, AlertaVigia, AnuncioVigiado, Canal } from './vigiaService'

export const LOJAS: Record<string, string> = {
  ml_matriz: 'ML Matriz',
  ml_extrema: 'ML Extrema',
  shopee_festa: 'Shopee Festa',
  shopee_decora: 'Shopee Decora',
  shein: 'Shein',
  tiktok: 'TikTok',
  amazon: 'Amazon',
}

export const ROTULO_ACAO: Record<Acao, (marketplace: string) => string> = {
  pausar_anuncio: (m) =>
    ({ shopee: 'Pausar na Shopee', shein: 'Tirar do ar na Shein', tiktok: 'Desativar no TikTok', amazon: 'Zerar estoque na Amazon' })[m] ||
    'Pausar no ML',
  zerar_variacao: () => 'Zerar só a variação',
}

export const loja = (k: string) => LOJAS[k] || k

/** "Full" no ML, "FBA" na Amazon. */
export const logistica = (a: Pick<AnuncioVigiado, 'full' | 'marketplace'>) => (a.full ? (a.marketplace === 'amazon' ? 'FBA' : 'Full') : '')

/** Texto do confirm antes de mexer no marketplace (o que muda de fato em cada canal). */
export function avisoConfirmacao(a: Pick<AnuncioVigiado, 'marketplace' | 'item_id' | 'loja' | 'sku_anuncio' | 'e_kit' | 'skus_vigiados' | 'variacao'>, acao: Acao): string {
  if (acao === 'zerar_variacao') return `Zerar só a variação do anúncio ${a.item_id} no marketplace AGORA?

Loja ${loja(a.loja)} — ${descricaoSku(a)}`
  const efeito: Record<string, string> = {
    shein: 'Isto tira a cor (SKC) inteira do ar na Shein Brasil — todos os tamanhos dela. A Shein só aceita nova mudança na mesma cor depois de 20 min.',
    tiktok: 'Isto desativa o produto inteiro (todas as variações) no TikTok.',
    amazon: 'Isto zera o estoque de envio próprio deste SKU na Amazon.',
  }
  const texto = efeito[a.marketplace] || `Isto muda o anúncio inteiro (todas as variações) na loja ${loja(a.loja)}.`
  return `${ROTULO_ACAO[acao](a.marketplace)}: anúncio ${a.item_id} AGORA?

${texto}`
}

/** Alerta aberto sem botão: o que fazer à mão. */
export function semAcao(a: Pick<AnuncioVigiado, 'marketplace' | 'full'> & { trava_campanha?: boolean }): string {
  if (a.trava_campanha) return 'Travado por campanha na Shein: tire do ar pela central da Shein.'
  if (a.marketplace === 'amazon' && a.full) return 'Anúncio no FBA (estoque na Amazon): resolva pela Seller Central.'
  return 'Sem ação pela API para este anúncio.'
}

/** "28/10 15:04" no fuso de São Paulo; null → "—". */
export function quando(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/** Separa a lista colada (vírgula, ponto e vírgula, espaço ou quebra de linha), em maiúsculas e sem repetir. */
export function separarSkus(texto: string): string[] {
  return [...new Set(texto.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean))]
}

export type FiltroAnuncios = { texto: string; loja: string; soVendaveis: boolean }

export function filtrarAnuncios(itens: AnuncioVigiado[], f: FiltroAnuncios): AnuncioVigiado[] {
  const t = f.texto.trim().toLowerCase()
  return itens.filter((a) => {
    if (f.soVendaveis && !a.vendavel) return false
    if (f.loja && a.loja !== f.loja) return false
    if (!t) return true
    return [a.sku_anuncio, a.item_id, a.titulo, ...a.skus_vigiados].some((x) => (x || '').toLowerCase().includes(t))
  })
}

/** Linha de identificação do anúncio no popup e nas tabelas: "KITBOLCOL-1 (kit de BOLCOLVER) · Verde". */
export function descricaoSku(a: Pick<AnuncioVigiado, 'sku_anuncio' | 'e_kit' | 'skus_vigiados' | 'variacao'>): string {
  const base = a.e_kit ? `${a.sku_anuncio} (kit de ${a.skus_vigiados.join(', ')})` : a.sku_anuncio
  return a.variacao ? `${base} · ${a.variacao}` : base
}

/** Canais que falharam na última leitura — a tela avisa, porque alerta de canal que falhou não fecha. */
export function canaisComFalha(canais: Record<string, Canal> | undefined): string[] {
  return Object.entries(canais || {})
    .filter(([, c]) => !c.ok)
    .map(([k, c]) => `${loja(k.split(':')[1] || k)}: ${c.erro || 'falhou'}`)
}

export function ultimaAcao(a: AlertaVigia): string {
  const x = a.acoes[a.acoes.length - 1]
  if (!x) return '—'
  const nome = x.tipo === 'ciente' ? 'Ciente' : ROTULO_ACAO[x.tipo](a.marketplace)
  return `${nome} · ${x.ok ? 'ok' : 'falhou'} · ${x.por} · ${quando(x.em)}${x.ok ? '' : ` — ${x.mensagem}`}`
}
